import { createRoute, useNavigate, Link } from '@tanstack/react-router';
import { Route as rootRoute } from './__root';
import { useState, FormEvent } from 'react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signInWithPopup, updateProfile } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';
import { useAuthStore } from '../store';
import { Helmet } from 'react-helmet-async';
import { LogIn, Mail, Lock, UserPlus, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

function LoginPage() {
  const [isSignUp, setIsSignUp] = useState(false);
  const loginStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": isSignUp ? "Create an Account - Cardston Market" : "Sign In - Cardston Market",
    "description": isSignUp ? "Join Cardston Market to shop local goods and sell your own creations." : "Sign in to your Cardston Market account to manage orders and message vendors.",
    "url": "https://www.cardstonmarket.com/login",
    "isPartOf": { "@id": "https://www.cardstonmarket.com/#website" }
  };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { fetchProfile } = useAuthStore();

  const handleAuthSuccess = async (user: any) => {
    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      let isAdmin = false;
      let role = 'user';

      if (userSnap.exists()) {
        const data = userSnap.data();
        isAdmin = data.isAdmin || false;
        role = data.role || 'user';
      }

      const userData: any = {
        email: user.email,
        lastLogin: serverTimestamp(),
      };

      if (!userSnap.exists()) {
        userData.createdAt = serverTimestamp();
        if (user.displayName) {
          userData.displayName = user.displayName;
        }
      }

      if (isSignUp && displayName) {
        userData.displayName = displayName;
      }

      await setDoc(userRef, userData, { merge: true });
      await fetchProfile(user.uid);

      if (isAdmin) {
        navigate({ to: '/admin' });
      } else {
        navigate({ to: '/' });
      }
    } catch (err) {
      console.error('Error handling auth success:', err);
      setError('Failed to process login.');
    }
  };

  const handleAuth = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (email.includes('+')) {
      setError('Emails containing "+" are not allowed.');
      return;
    }

    setLoading(true);
    try {
      if (isSignUp) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        if (displayName) {
           await updateProfile(userCredential.user, { displayName });
        }
        await handleAuthSuccess(userCredential.user);
        toast.success('Account created successfully!');
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        await handleAuthSuccess(userCredential.user);
      }
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      
      if (userCredential.user.email?.includes('+')) {
        await userCredential.user.delete().catch(console.error); // Optional: delete the created auth record if it's a new signup
        await auth.signOut();
        setError('Emails containing "+" are not allowed.');
        return;
      }
      
      await handleAuthSuccess(userCredential.user);
    } catch (err: any) {
      setError(err.message || 'Failed to log in with Google');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-neutral-50 dark:bg-neutral-900 transition-colors duration-300">
      <Helmet>
        <title>{isSignUp ? 'Create an Account' : 'Sign In'} - Cardston Market</title>
        <meta name="description" content={isSignUp ? "Join Cardston Market to shop local goods and sell your own creations. Create your account today." : "Sign in to your Cardston Market account to manage orders and message vendors."} />
        <meta name="robots" content="noindex, nofollow" />
        <script type="application/ld+json">{JSON.stringify(loginStructuredData)}</script>
      </Helmet>
      <div className="max-w-md w-full space-y-8 bg-white dark:bg-neutral-900 p-8 sm:p-10 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-xl shadow-neutral-100/50 dark:shadow-none">
        <div className="text-center">
          <h2 className="text-3xl font-serif font-medium text-neutral-900 dark:text-white tracking-tight">
            {isSignUp ? 'Create an Account' : 'Welcome Back'}
          </h2>
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
            {isSignUp ? 'Join Cardston Market today' : 'Sign in to access your account'}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleAuth}>
          {error && (
            <div className="bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 p-4 rounded-xl">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
          <div className="space-y-4">
            {isSignUp && (
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <UserPlus className="h-5 w-5 text-neutral-400 stroke-[1.5]" />
                </div>
                <input
                  type="text"
                  required
                  autoComplete="name"
                  className="appearance-none relative block w-full px-4 py-3.5 pl-11 border border-neutral-200 dark:border-neutral-800 placeholder-neutral-400 text-neutral-900 dark:text-white rounded-xl focus:outline-none focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white sm:text-sm transition-colors bg-neutral-50 dark:bg-neutral-950 focus:bg-white dark:focus:bg-neutral-900"
                  placeholder="Full Name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </div>
            )}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-neutral-400 stroke-[1.5]" />
              </div>
              <input
                type="email"
                required
                autoComplete="email"
                className="appearance-none relative block w-full px-4 py-3.5 pl-11 border border-neutral-200 dark:border-neutral-800 placeholder-neutral-400 text-neutral-900 dark:text-white rounded-xl focus:outline-none focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white sm:text-sm transition-colors bg-neutral-50 dark:bg-neutral-950 focus:bg-white dark:focus:bg-neutral-900"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-neutral-400 stroke-[1.5]" />
              </div>
              <input
                type="password"
                required
                autoComplete={isSignUp ? "new-password" : "current-password"}
                className="appearance-none relative block w-full px-4 py-3.5 pl-11 border border-neutral-200 dark:border-neutral-800 placeholder-neutral-400 text-neutral-900 dark:text-white rounded-xl focus:outline-none focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white sm:text-sm transition-colors bg-neutral-50 dark:bg-neutral-950 focus:bg-white dark:focus:bg-neutral-900"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3.5 px-4 border border-transparent text-sm font-medium rounded-xl text-white dark:text-neutral-900 bg-neutral-900 dark:bg-white hover:bg-neutral-800 dark:hover:bg-neutral-200 transition-all duration-300 disabled:opacity-50 shadow-lg shadow-neutral-200 dark:shadow-none"
            >
              {loading ? (isSignUp ? 'Creating account...' : 'Signing in...') : (isSignUp ? 'Create Account' : 'Sign In')}
            </button>
          </div>
        </form>

        <div className="mt-6 flex items-center justify-center">
          <button
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors flex items-center gap-1"
          >
            {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>

        <div className="mt-8">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-neutral-200 dark:border-neutral-800" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white dark:bg-neutral-900 text-neutral-500 dark:text-neutral-400">Or continue with</span>
            </div>
          </div>

          <div className="mt-8">
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full flex justify-center items-center py-3.5 px-4 border border-neutral-200 dark:border-neutral-800 rounded-xl bg-white dark:bg-neutral-900 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-50"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Google
            </button>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-neutral-100 dark:border-neutral-800 text-center">
           <p className="text-sm text-neutral-500 dark:text-neutral-400">
             Interested in selling your own products?
           </p>
           <Link 
             to="/sell" 
             className="mt-2 inline-block text-sm font-bold text-neutral-900 dark:text-white hover:underline transition-all"
           >
             Learn how to become a vendor
           </Link>
        </div>
      </div>
    </div>
  );
}
